'use client';

import { WalletName, WalletReadyState } from '@solana/wallet-adapter-base';
import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight } from '@drift-labs/icons';
import useWalletsToDisplay, {
	WalletToDisplayInfo,
} from 'src/hooks/useWalletsToDisplay';
import { DriftWalletName } from 'src/constants/wallets';
import { openUrl } from 'src/utils/browser';
import MarketIcon from '../Utils/MarketIcon';
import { WALLET_BUTTON_ICON_CLASSNAME, WalletButton } from './WalletButton';
import { EmailWalletsList } from './EmailWalletsList';
import ConnectWalletModalButton from 'src/components/Modals/Onboarding/ConnectWalletModal/ConnectWalletModalButton';

export const MoreWalletsCta = (props: {
	onClick: () => void;
	wallets: WalletToDisplayInfo[];
}) => {
	return (
		<button
			className="group flex w-full items-center justify-between text-text-secondary hover:text-text-default"
			onClick={props.onClick}
		>
			<span className="typo-b3">More wallets</span>
			<ChevronRight size={20} />
		</button>
	);
};

const OtherWalletsDropdown = (props: {
	wallets: WalletToDisplayInfo[];
	onChooseWallet?: (selection: string) => void;
}) => {
	const contentDivRef = useRef<HTMLDivElement>(null);
	const [expanded, setExpanded] = useState(false);
	const [maxHeight, setMaxHeight] = useState(0);

	const toggleExpanded = () => {
		setExpanded(!expanded);
	};

	useEffect(() => {
		if (contentDivRef?.current?.offsetHeight) {
			setMaxHeight(contentDivRef.current?.offsetHeight);
		}
	}, [contentDivRef.current?.offsetHeight]);

	return (
		<div className="w-full">
			<MoreWalletsCta onClick={toggleExpanded} wallets={props.wallets} />

			<div
				className="w-full overflow-hidden transition-[max-height] duration-300"
				style={{ maxHeight: expanded ? `${maxHeight}px` : '0px' }}
			>
				<div ref={contentDivRef} className="flex flex-col gap-y-md pt-4">
					<EmailWalletsList
						onChooseWallet={(walletName: DriftWalletName) =>
							props.onChooseWallet?.(walletName as string)
						}
					/>
					{props.wallets.map((walletProvider, index) => (
						<WalletButton
							onChooseWallet={() =>
								props.onChooseWallet?.(
									walletProvider.provider.adapter.name ?? ''
								)
							}
							wallet={walletProvider}
							key={(walletProvider.provider.adapter.name ?? '') + index}
						/>
					))}
				</div>
			</div>
		</div>
	);
};

const ConnectWalletButtonsList = (props: {
	showUndetectedWallets?: boolean;
	numberOfWalletsToShow?: number;
	onSelectedWallet?: (walletName: DriftWalletName) => void;
}) => {
	const { walletsToShow, hiddenWallets } = useWalletsToDisplay(
		props.numberOfWalletsToShow
	);

	const handleSelectWallet = (name: DriftWalletName) => {
		props.onSelectedWallet?.(name);
	};

	const hasNoInstalledWallet =
		walletsToShow.length === 0 ||
		!walletsToShow.find(
			(wallet) => wallet.provider.readyState === WalletReadyState.Installed
		);

	return (
		<>
			<div className="mb-4 typo-b3">Select a wallet</div>

			{hasNoInstalledWallet && (
				<ConnectWalletModalButton
					onClick={() =>
						openUrl('https://www.drift.trade/learn/best-solana-wallets')
					}
				>
					<div className={WALLET_BUTTON_ICON_CLASSNAME}>
						<MarketIcon marketSymbol="SOL" className="size-full" />
					</div>
					Get a Solana Wallet
				</ConnectWalletModalButton>
			)}

			{/* Show regular Wallets to Show */}
			{walletsToShow.map((walletProvider) => (
				<WalletButton
					wallet={walletProvider}
					onChooseWallet={() =>
						handleSelectWallet(walletProvider.provider.adapter.name)
					}
					key={walletProvider.provider.adapter.name}
				/>
			))}

			{!props.showUndetectedWallets && hiddenWallets.length > 0 && (
				<OtherWalletsDropdown
					wallets={hiddenWallets}
					onChooseWallet={(walletName) =>
						handleSelectWallet(walletName as WalletName)
					}
				/>
			)}
		</>
	);
};

export default React.memo(ConnectWalletButtonsList);
